import { rem } from 'polished'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import TopContent from '../Common/TopContent'
import type { ItemCardVariant } from '../types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FullArrow from 'components/utils/Arrows/FullArrow'
import Group from 'components/utils/Group'
import PrintOnly from 'components/utils/PrintOnly'
import ItemLocationSummary from 'tripPlanner/components/ItemCard/Common/ItemLocationSummary'
import { ItemMetadata } from 'tripPlanner/models'
import type { TransportItem } from 'tripPlanner/types/tripItem'
import { calculateTravelTimeLabel } from 'tripPlanner/utils'

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(min-content, max-content) 1fr minmax(
      min-content,
      max-content
    );
  grid-gap: ${rem(16)};
  justify-content: space-between;
  align-items: center;
`

interface Props {
  item: TransportItem
  variant: ItemCardVariant
}

function TransportItemContent({ item, variant }: Props) {
  const intl = useIntl()
  const {
    type,
    startPlace,
    endPlace,
    confirmationCode,
    startUtcDateTime,
    endUtcDateTime,
  } = item

  const transportTypeName = intl.formatMessage(ItemMetadata[type].singularName)
  const title = item.operatorName
    ? intl.formatMessage(
        {
          id: 'dDlsZesDwxRmQPLPE0VP',
          defaultMessage: '{transportType} · {operatorName}',
          description: 'Transport item title with operator name separated by middle dot',
        },
        { transportType: transportTypeName, operatorName: item.operatorName },
      )
    : transportTypeName
  const duration = calculateTravelTimeLabel(startUtcDateTime, endUtcDateTime)
  return (
    <Group direction="vertical" gap={12} fullWidth>
      <TopContent item={item} variant={variant}>
        {title}
      </TopContent>
      {confirmationCode && (
        <PrintOnly>
          <BodyText variant="medium">
            <FormattedMessage
              id="B18fbiBuaYQK9WmQPJKP"
              defaultMessage="<bold>Confirmation number:</bold> {code}"
              description="Transport booking confirmation number with label in the trip planner"
              values={{
                bold: (chunks) => <BodyText as="span" variant="medium" weight="bold">{chunks}</BodyText>,
                code: confirmationCode,
              }}
            />
          </BodyText>
        </PrintOnly>
      )}
      <Layout>
        <ItemLocationSummary
          place={startPlace}
          date={item.startDate}
          time={item.startTime}
          day={item.startDay}
          canShowWarning
          item={item}
          variant={variant}
        />
        <VerticalSpacer gap={4}>
          <Caption align="center" variant="medium">
            {duration}
          </Caption>
          <FullArrow height={1} />
        </VerticalSpacer>
        <ItemLocationSummary
          place={endPlace}
          date={item.endDate}
          time={item.endTime}
          day={item.endDay}
          item={item}
          variant={variant}
        />
      </Layout>
    </Group>
  )
}

export default TransportItemContent
